import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/lamka";
import { useAuth } from "@/context/AuthContext";

const pickCount = (d) => (typeof d === "number" ? d : d?.count ?? d?.unread_count ?? d?.unread ?? 0);

export const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!user) return setCount(0);
    api.get("/notifications/unread-count").then((r) => setCount(pickCount(r.data))).catch(() => {});
  }, [user]);

  const onOpen = async (open) => {
    if (!open || !user) return;
    try {
      const { data } = await api.get("/notifications");
      setItems(Array.isArray(data) ? data : data.notifications || data.items || []);
      if (count) {
        await api.post("/notifications/read");
        setCount(0);
      }
    } catch {}
  };

  const btn = "relative p-2 min-[350px]:p-2.5 text-white/80 hover:text-[#C5A059] transition-colors";
  if (!user)
    return (
      <button data-testid="nav-notifications-button" aria-label="Notifications" className={btn} onClick={() => navigate("/login")}>
        <Bell className="h-5 w-5" strokeWidth={1.5} />
      </button>
    );

  return (
    <Popover onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button data-testid="nav-notifications-button" aria-label="Notifications" className={btn}>
          <Bell className="h-5 w-5" strokeWidth={1.5} />
          {count > 0 && (
            <span data-testid="notifications-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C5A059] text-[#0F0F0F] text-[10px] font-mono font-bold flex items-center justify-center">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" data-testid="notifications-panel" className="w-80 rounded-none border-[#C5A059]/30 bg-[#141414] text-white p-0">
        <p className="eyebrow px-5 py-4 border-b border-white/10">Notifications</p>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p data-testid="notifications-empty" className="px-5 py-8 text-sm text-white/50">You're all caught up.</p>
          ) : (
            items.slice(0, 30).map((n, i) => (
              <div key={n.id || i} data-testid="notification-item" className="px-5 py-4 border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                <p className="text-sm font-medium">{n.title || "Update"}</p>
                {(n.body || n.message) && <p className="text-xs text-white/55 mt-1 line-clamp-2">{n.body || n.message}</p>}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
